import { EmoNeutral, Market, Pack } from "@assets"
import { Head, Link, router } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { orderPath, ordersPath } from "@/routes"

interface OrderItem {
  id: number
  quantity: number
  product: { id: number; name?: string; title?: string }
}

interface Order {
  id: number
  order_number: string
  status: string
  created_at: string
  business: { id: number; business_name: string }
  order_items: OrderItem[]
  total: number
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  orders: Order[]
  filters: { status?: string }
  pagination: Pagination
  status_counts: Record<string, number>
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const statusLabel: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  preparing: "Preparando",
  ready: "Listo",
  delivering: "En camino",
  completed: "Completado",
  cancelled: "Cancelado",
}

const statusVariant: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  confirmed: "outline",
  preparing: "outline",
  ready: "outline",
  delivering: "outline",
  completed: "default",
  cancelled: "destructive",
}

const shortOrderRef = (orderNumber: string) => {
  const parts = orderNumber.split("-")
  const last = parts[parts.length - 1] ?? orderNumber
  const short = last.slice(-6)
  return short.toUpperCase()
}

export default function OrdersIndex({ orders, filters, status_counts }: Props) {
  const hasOrders = (orders?.length ?? 0) > 0
  const totalOrders = Object.values(status_counts || {}).reduce(
    (a, b) => a + b,
    0,
  )

  const handleStatusFilter = (value: string) => {
    const status = value === "all" ? undefined : value
    router.get(ordersPath(), { status }, { preserveState: true })
  }

  return (
    <AppLayout>
      <Head title="Mis pedidos" />
      <div className="mx-auto max-w-4xl space-y-4 py-4 md:py-6">
        <div className="flex items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-2">
            <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
              <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                <Pack className="fill-primary h-8 w-8" />
              </span>
              Mis pedidos
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={filters.status ?? "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-auto">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos ({totalOrders})</SelectItem>
                <SelectItem value="pending">
                  Pendientes ({status_counts?.pending || 0})
                </SelectItem>
                <SelectItem value="confirmed">
                  Confirmados ({status_counts?.confirmed || 0})
                </SelectItem>
                <SelectItem value="preparing">
                  Preparando ({status_counts?.preparing || 0})
                </SelectItem>
                <SelectItem value="ready">
                  Listos ({status_counts?.ready || 0})
                </SelectItem>
                <SelectItem value="delivering">
                  En camino ({status_counts?.delivering || 0})
                </SelectItem>
                <SelectItem value="completed">
                  Completados ({status_counts?.completed || 0})
                </SelectItem>
                <SelectItem value="cancelled">
                  Cancelados ({status_counts?.cancelled || 0})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!hasOrders && (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin productos" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              Aún no tienes pedidos
            </h3>
          </div>
        )}

        <div className="space-y-3">
          {orders?.map((o) => (
            <Link
              key={o.id}
              href={orderPath(o.id)}
              className="border-chart-4 block w-full border-t-[1px] p-[10px]"
            >
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-muted-foreground font-mono text-sm">
                      Pedido #{shortOrderRef(o.order_number)}
                    </span>
                    <Badge variant={statusVariant[o.status] || "default"}>
                      {statusLabel[o.status] ?? o.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <Market className="h-4 w-4" />
                    <span className="truncate font-medium">
                      {o.business.business_name}
                    </span>
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {o.order_items
                      ?.slice(0, 2)
                      .map((it) => it.product.name ?? it.product.title)
                      .join(", ") +
                      ((o.order_items?.length ?? 0) > 2 ? "…" : "")}
                  </div>

                  <div className="text-muted-foreground mt-1 text-xs">
                    {formatDate(o.created_at)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-lg font-semibold">
                    {formatPrice(o.total)}
                  </div>
                </div>
              </CardContent>
            </Link>
          ))}
        </div>
      </div>
    </AppLayout>
  )
}
